// Higher levels favour retaining quality. Fatal decode/source errors bypass this policy.
export const DOWNGRADE_LEVEL_STORAGE_KEY = "seaking.playback.downgrade-level.v1";
export const DEFAULT_DOWNGRADE_LEVEL = 7;

export const normalizeDowngradeLevel = (value: number) =>
  Number.isFinite(value) ? Math.max(1, Math.min(10, Math.round(value))) : DEFAULT_DOWNGRADE_LEVEL;

export const playbackDowngradePolicy = (value: number) => {
  const level = normalizeDowngradeLevel(value);
  return {
    level,
    bufferSeconds: Number((3 - (level - 1) * 0.25).toFixed(2)),
    pressureMs: (2 + (level - 1) * 2) * 1000,
    bitrateRatio: 1.05 + (level - 1) * 0.05,
    cooldownMs: 30_000 + (level - 1) * 5_000,
  };
};

export const readDowngradeLevel = () => {
  try {
    if (typeof window === "undefined") return DEFAULT_DOWNGRADE_LEVEL;
    const stored = window.localStorage.getItem(DOWNGRADE_LEVEL_STORAGE_KEY);
    return stored === null ? DEFAULT_DOWNGRADE_LEVEL : normalizeDowngradeLevel(Number(stored));
  } catch {
    return DEFAULT_DOWNGRADE_LEVEL;
  }
};

export type DowngradePressure = { since: number | null; lastSampleAt: number | null };

export const evaluateDowngradePressure = ({
  level, now, pressure, bandwidthBps, bitrateBps, bufferSeconds,
  suspended, sinceSwitchMs,
}: {
  level: number;
  now: number;
  pressure: DowngradePressure;
  bandwidthBps: number;
  bitrateBps: number;
  bufferSeconds: number;
  suspended: boolean;
  sinceSwitchMs: number;
}) => {
  const policy = playbackDowngradePolicy(level);
  const underPressure = !suspended && Number.isFinite(bandwidthBps) && bandwidthBps > 0 &&
    Number.isFinite(bitrateBps) && bitrateBps > bandwidthBps * policy.bitrateRatio &&
    Number.isFinite(bufferSeconds) && bufferSeconds < policy.bufferSeconds &&
    sinceSwitchMs >= policy.cooldownMs;
  if (!underPressure) {
    pressure.since = null;
    pressure.lastSampleAt = null;
    return false;
  }
  // Do not count background timer gaps as continuously observed pressure.
  if (pressure.lastSampleAt === null || now - pressure.lastSampleAt > 3000) pressure.since = now;
  pressure.lastSampleAt = now;
  if (pressure.since === null) pressure.since = now;
  return now - pressure.since >= policy.pressureMs;
};
